/**
 * Header company switcher — the company this tab is working in.
 *
 * Sits on the right of the header, before the user. Choosing another company
 * reloads the page in that company (see services/activeCompany.js for why a
 * reload, and apps/core/company_context.py for what the server does with it).
 */
import React, { useState } from 'react';
import {
  Box, ButtonBase, Typography, Menu, MenuItem, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  Tooltip, Divider,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import api from '../../services/api';
import {
  useActiveCompany, switchActiveCompany, companyColour,
} from '../../services/activeCompany';

const SOURCE_TEXT = {
  last_used: 'the company you last worked in',
  data_access: 'the first company assigned to you in Data Access',
  first_active: 'the first active company',
};

function Mark({ company, size = 10 }) {
  return (
    <Box component="span" sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      bgcolor: companyColour(company),
    }} />
  );
}

export default function CompanySwitcher() {
  const { companies, active, defaultSource } = useActiveCompany();
  const [anchor, setAnchor] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!active) return null;

  const canSwitch = companies.length > 1;

  const confirmSwitch = async () => {
    setBusy(true);
    setError('');
    try {
      await switchActiveCompany(api, pending.id);   // reloads the page
    } catch (e) {
      setBusy(false);
      setError(e?.response?.data?.detail || e?.response?.data?.company?.[0]
        || 'Could not switch company.');
    }
  };

  return (
    <>
      <Tooltip title={canSwitch ? 'Change company' : 'The only company you are authorised for'}>
        <ButtonBase
          onClick={canSwitch ? (e) => setAnchor(e.currentTarget) : undefined}
          disableRipple={!canSwitch}
          aria-label={`Company: ${active.name}`}
          aria-haspopup={canSwitch ? 'menu' : undefined}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            height: 34, px: 1.25, mx: 0.5, borderRadius: 1,
            color: '#fff', border: '1px solid rgba(255,255,255,0.22)',
            cursor: canSwitch ? 'pointer' : 'default',
            '&:hover': canSwitch ? { bgcolor: 'rgba(255,255,255,0.08)' } : {},
          }}
        >
          <Mark company={active} />
          <Typography
            noWrap
            sx={{
              display: { xs: 'none', md: 'block' },
              fontSize: '0.85rem', fontWeight: 500, maxWidth: { md: 220, lg: 300 },
            }}
          >
            {active.name}
          </Typography>
          {canSwitch && <ArrowDropDownIcon fontSize="small" sx={{ ml: -0.5 }} />}
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 300, maxHeight: 440, mt: 0.5 } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
          <Typography variant="subtitle2">Company</Typography>
          {defaultSource && (
            <Typography variant="caption" color="text.secondary">
              New tabs open in {SOURCE_TEXT[defaultSource] || 'your default company'}.
            </Typography>
          )}
        </Box>
        <Divider />
        {companies.map((c) => {
          const current = c.id === active.id;
          return (
            <MenuItem
              key={c.id}
              selected={current}
              onClick={() => {
                setAnchor(null);
                if (!current) setPending(c);
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {current ? <CheckIcon fontSize="small" /> : <Mark company={c} size={10} />}
              </ListItemIcon>
              <ListItemText primary={c.name} primaryTypographyProps={{ fontSize: '0.9rem' }} />
            </MenuItem>
          );
        })}
      </Menu>

      <Dialog open={Boolean(pending)} onClose={() => !busy && setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Switch to {pending?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This tab will work in {pending?.name}: its GL accounts, ledgers and documents, and new
            documents will be created in it. The page reloads, so unsaved changes on it are lost.
            Other open tabs keep their own company.
          </DialogContentText>
          {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={confirmSwitch} disabled={busy}>
            {busy ? 'Switching…' : 'Switch company'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
